import React, {Component} from "react";
import {Row, Col} from 'reactstrap';
import {Calculator} from "../Calculator";
import {Scatter} from "react-chartjs-2";
import PropTypes from "prop-types";
import 'chartjs-plugin-datalabels';
import {Strings} from "../Strings";
import {Constants} from "../Constants";

export class ValveCapacityChart extends Component {
    constructor(props) {
        super(props);

        this.generateAlternatives = this.generateAlternatives.bind(this);
        this.chartClicked = this.chartClicked.bind(this);

        this.valveDimensions = [];
        for (const dimension in Calculator.kvValues[0]) {
            this.valveDimensions.push(dimension);
        }

        let capacityLiftData = [];
        let i;
        for (i = 0; i <= 100; i++) {
            //From excel doc: = -0,0000021507684*V20^4 + 0,000560016315*V20^3 - 0,04941995824*V20^2 + 2,492627131033*V20
            capacityLiftData[i] = {
                x: i,
                y: -0.0000021507684 * Math.pow(i, 4) + 0.000560016315 * Math.pow(i, 3) - 0.04941995824 * Math.pow(i, 2) + 2.492627131033 * i
            }
        }
        this.baseDataset = [
            {
                label: Strings.valveCapacityCharacteristic,
                data: capacityLiftData,
                fill: false,
                pointRadius: 0,
                showLine: true,
                borderColor: Constants.avkBlue,
                backgroundColor: Constants.invisible,
                pointStyle: "line",
            },
            {
                label: Strings.valveCapacityDimensionalArea,
                data: [{x: Constants.opAreaMin, y: 101}, {x: Constants.opAreaMin, y: -1},
                    {x: Constants.opAreaMax, y: -1}, {x: Constants.opAreaMax, y: 101}],
                fill: false,
                pointRadius: 0,
                showLine: true,
                borderDash: [5, 5],
                borderColor: Constants.capChartOpAreaColor,
                lineTension: 0,
                backgroundColor: Constants.invisible,
                pointStyle: "line",
            }
        ];
        this.options = {
            events: [],
            tooltips: {
                enabled: false
            },
            defaultFontFamily: Constants.chartFontFamily,
            legend: {
                display: true,
                position: "bottom",
                labels: {
                    usePointStyle: true,
                    filter: function (item, chart) {
                        if (!item.text) {
                            return false;
                        }
                        return !item.text.includes('hide');
                    }
                },

            },
            title: {
                display: false,
                text: Strings.titleValveCapacityChart
            },
            scales: {
                xAxes: [{
                    position: 'bottom',
                    scaleLabel: {
                        display: true,
                        labelString: Strings.valveCapacityX
                    },
                    ticks: {
                        min: 0,
                        max: 100,
                        stepSize: 10
                    }
                }],
                yAxes: [{
                    display: true,
                    scaleLabel: {
                        display: true,
                        labelString: Strings.valveCapacityY
                    },
                    ticks: {
                        min: 0,
                        max: 100,
                        stepSize: 10,
                        minor: {
                            display: true
                        }
                    }
                }]
            },
            plugins: {
                datalabels: {
                    display: false,
                    align: 'end',
                    anchor: 'end',
                    textAlign: "center",
                }
            },
        };
    }

    generateAlternatives() {
        const inputData = this.props.inputData;
        const hideAll = Calculator.calcPressureDifference(inputData) === null;
        let alternatives = [];

        for (let i = 0; i < this.valveDimensions.length; i++) {
            let newInputData = Object.assign({}, inputData);
            newInputData.valveDimension = this.valveDimensions[i];

            if (Calculator.kvValues[newInputData.valveBore][newInputData.valveDimension] != null) {
                alternatives.push(this.newPoint(newInputData, true));
            }

            newInputData.valveBore = (inputData.valveBore === Constants.REDUCED) ? Constants.FULL : Constants.REDUCED;
            if (Calculator.kvValues[newInputData.valveBore][newInputData.valveDimension] != null) {
                alternatives.push(this.newPoint(newInputData, false));
            }
        }

        alternatives.sort(
            function (a, b) {
                return (a.key + a.bore) - (b.key + b.bore);
            }
        );

        let selectedOptimal = {x: 0};

        for (let key in alternatives) {
            if (alternatives[key].x < Constants.opAreaMin || alternatives[key].x > Constants.opAreaMax || alternatives[key].hasWarnings || hideAll) {
                alternatives[key].visible = false;
            }

            if (alternatives[key].x > selectedOptimal.x && alternatives[key].x < Constants.opAreaMax && alternatives[key].visible) {
                selectedOptimal = alternatives[key];
            }

            if (alternatives[key].key === inputData.valveDimension && alternatives[key].bore === inputData.valveBore && !hideAll) {
                alternatives[key].selected = true;
            }
        }
        selectedOptimal.label = Strings.valveCapacityOptimalSize + selectedOptimal.label;

        return alternatives;
    }

    newPoint(newInputData, isVisible) {
        return {
            x: Calculator.calcValveCapacity(newInputData),
            y: Calculator.calcPlugLift(newInputData),
            label: Strings.valveCapacityLabelPrefix + newInputData.valveDimension,
            selected: false,
            key: Number(newInputData.valveDimension),
            bore: Number(newInputData.valveBore),
            visible: isVisible,
            hasWarnings: Calculator.shouldShowFlowVelocityMessage(newInputData)
        };
    }

    render() {
        let newDataset = this.baseDataset.slice(0);
        this.alternatives = this.generateAlternatives();

        let pointColors = [];
        let pointBorderColors = [];
        let pointTextColors = [];
        let pointRadius = [];
        let pointBorderWidths = [];

        for (let key in this.alternatives) {
            let pointProperties = Constants.pointInvisibleProps;
            if (this.alternatives[key].visible) {
                pointProperties = Constants.pointVisibleProps;
            }
            if (this.alternatives[key].selected) {
                pointProperties = Constants.pointSelectedProps;
            }

            pointColors.push(pointProperties.color);
            pointBorderColors.push(pointProperties.borderColor);
            pointTextColors.push(pointProperties.textColor);
            pointRadius.push(pointProperties.radius);
            pointBorderWidths.push(pointProperties.borderWidth);
        }

        newDataset.unshift(
            {
                label: "Possibilities hide",
                data: this.alternatives,
                pointBackgroundColor: pointColors,
                pointBorderColor: pointBorderColors,
                pointBorderWidth: pointBorderWidths,
                pointRadius: pointRadius,
                hitRadius: 0,
                datalabels: {
                    display: true,
                    color: pointTextColors
                }
            });

        return <Col lg="8" className="botSpace">
            <div className="h-100 d-flex flex-column">
                <Row>
                    <Col><h2>Valve capacity chart</h2></Col>
                </Row>
                <Row className="flex-grow-1">
                    <Col className="d-flex ">
                        <div className="capChart block d-flex align-items-center flex-grow-1">
                            <Scatter data={{datasets: newDataset}} options={this.options}
                                     width={Constants.capChartRatio[0]} height={Constants.capChartRatio[1]}
                                     getElementsAtEvent={this.chartClicked}/>
                        </div>
                    </Col></Row>
            </div>
        </Col>
    }

    chartClicked(elems) {
        if (elems.length > 0) {
            for (let key in elems) {
                if (this.alternatives[elems[key]._index].visible) {
                    const inputData = this.props.inputData;
                    inputData.valveDimension = this.alternatives[elems[key]._index].key;
                    inputData.valveBore = this.alternatives[elems[key]._index].bore;
                    this.props.onChange(inputData);
                    break;
                }
            }
        }
    }
}

ValveCapacityChart.propTypes = {
    inputData: PropTypes.shape({
        flow: PropTypes.number,
        inletPressure: PropTypes.number,
        outletPressure: PropTypes.number,
        valveDimension: PropTypes.number,
        valveBore: PropTypes.number,
        description: PropTypes.string
    }).isRequired,
    onChange: PropTypes.func.isRequired
};